import React, { useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, Clock, MapPin, Briefcase, CheckCircle2, XCircle, CalendarClock, ChevronRight, ChevronLeft, Check } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface PendingAppointment {
  id: string;
  date: string;
  time: string;
  prospect_name: string | null;
  company_name: string | null;
  job_title: string | null;
  status: string;
  location: string | null;
  title: string | null;
}

interface Props {
  clientId: string;
  onNavigate: (id: string) => void;
}

const statusActions = [
  {
    value: 'effectué',
    label: 'Effectué',
    icon: CheckCircle2,
    bg: 'bg-emerald-50 hover:bg-emerald-100 border-emerald-200',
    text: 'text-emerald-700',
    iconColor: 'text-emerald-500',
  },
  {
    value: 'annulé',
    label: 'Annulé',
    icon: XCircle,
    bg: 'bg-red-50 hover:bg-red-100 border-red-200',
    text: 'text-red-700',
    iconColor: 'text-red-500',
  },
  {
    value: 'reporté',
    label: 'Reporté',
    icon: CalendarClock,
    bg: 'bg-amber-50 hover:bg-amber-100 border-amber-200',
    text: 'text-amber-700',
    iconColor: 'text-amber-500',
  },
];

const PendingAppointmentsCard: React.FC<Props> = ({ clientId, onNavigate }) => {
  const [appointments, setAppointments] = useState<PendingAppointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [justUpdated, setJustUpdated] = useState<string | null>(null);
  const [direction, setDirection] = useState(0);

  const fetchPending = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const today = new Date().toISOString().split('T')[0];

      const { data, error } = await supabase
        .from('appointments')
        .select('id, date, time, prospect_name, company_name, job_title, status, location, title')
        .eq('client_id', clientId)
        .in('status', ['planifié', 'Scheduled'])
        .lt('date', today)
        .order('date', { ascending: false })
        .order('time', { ascending: false })
        .limit(20);

      if (error) throw error;
      setAppointments(data || []);
    } catch (err) {
      console.error('Error fetching pending appointments:', err);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    fetchPending();
  }, [fetchPending]);

  const handleStatusChange = async (appointmentId: string, newStatus: string) => {
    setUpdatingId(appointmentId);
    try {
      const { error } = await supabase
        .from('appointments')
        .update({ status: newStatus })
        .eq('id', appointmentId);

      if (error) throw error;

      setJustUpdated(newStatus);

      setTimeout(() => {
        setAppointments((prev) => prev.filter((a) => a.id !== appointmentId));
        setJustUpdated(null);
        setDirection(0);
      }, 1200);
    } catch (err) {
      console.error('Error updating status:', err);
    } finally {
      setUpdatingId(null);
    }
  };

  const currentIndex = 0;
  const apt = appointments[currentIndex];
  const pendingCount = appointments.length;

  if (loading) {
    return (
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-64 mb-2" />
        <div className="h-4 bg-gray-100 rounded w-80 mb-6" />
        <div className="h-32 bg-gray-50 rounded-xl" />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden"
    >
      <div className="p-6 pb-4">
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-lg font-semibold text-gray-900">
            Avez-vous effectué ces RDV ?
          </h2>
          {pendingCount > 0 && (
            <span className="text-xs font-semibold bg-amber-100 text-amber-700 px-2.5 py-1 rounded-full">
              {pendingCount} en attente
            </span>
          )}
        </div>
        <p className="text-sm text-gray-500">
          Mettez à jour le statut de vos rendez-vous passés
        </p>
      </div>

      {pendingCount === 0 ? (
        <div className="px-6 pb-8">
          <div className="flex flex-col items-center justify-center py-10 bg-gray-50 rounded-xl">
            <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mb-3">
              <Check className="h-6 w-6 text-emerald-600" />
            </div>
            <p className="text-sm font-medium text-gray-700">Tout est à jour</p>
            <p className="text-xs text-gray-500 mt-1">Aucun rendez-vous en attente de confirmation</p>
          </div>
        </div>
      ) : (
        <div className="px-6 pb-6">
          <AnimatePresence mode="wait">
            {justUpdated ? (
              <motion.div
                key="updated"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.3 }}
                className="rounded-xl bg-emerald-50 border border-emerald-200 p-6 flex flex-col items-center justify-center"
              >
                <CheckCircle2 className="h-8 w-8 text-emerald-500 mb-2" />
                <span className="text-sm font-medium text-emerald-700">
                  Marqué comme {justUpdated}
                </span>
              </motion.div>
            ) : (
              <motion.div
                key={apt.id}
                initial={{ opacity: 0, x: direction >= 0 ? 60 : -60 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: direction >= 0 ? -60 : 60 }}
                transition={{ duration: 0.3 }}
                className="rounded-xl border bg-white border-gray-100"
              >
                <div className="p-5">
                  <div className="flex items-start justify-between gap-3 mb-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1.5">
                        <h4 className="text-base font-semibold text-gray-900 truncate">
                          {apt.prospect_name || apt.title || 'RDV'}
                        </h4>
                        <button
                          onClick={() => onNavigate(apt.id)}
                          className="shrink-0 text-gray-400 hover:text-blue-600 transition-colors"
                        >
                          <ChevronRight className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="flex items-center flex-wrap gap-x-4 gap-y-1.5">
                        {apt.company_name && (
                          <span className="flex items-center text-sm text-gray-500">
                            <Briefcase className="h-3.5 w-3.5 mr-1.5 text-gray-400" />
                            {apt.company_name}
                          </span>
                        )}
                        <span className="flex items-center text-sm text-gray-500">
                          <Calendar className="h-3.5 w-3.5 mr-1.5 text-gray-400" />
                          <span className="capitalize">
                            {new Date(apt.date).toLocaleDateString('fr-FR', {
                              weekday: 'short',
                              day: 'numeric',
                              month: 'short',
                            })}
                          </span>
                        </span>
                        {apt.time && (
                          <span className="flex items-center text-sm text-gray-500">
                            <Clock className="h-3.5 w-3.5 mr-1.5 text-gray-400" />
                            {apt.time}
                          </span>
                        )}
                        {apt.location && (
                          <span className="flex items-center text-sm text-gray-500">
                            <MapPin className="h-3.5 w-3.5 mr-1.5 text-gray-400" />
                            <span className="truncate max-w-[160px]">{apt.location}</span>
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {statusActions.map((action) => {
                      const Icon = action.icon;
                      return (
                        <button
                          key={action.value}
                          onClick={() => handleStatusChange(apt.id, action.value)}
                          disabled={!!updatingId}
                          className={`
                            flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg border text-sm font-medium
                            transition-all disabled:opacity-50 disabled:cursor-not-allowed
                            ${action.bg} ${action.text}
                          `}
                        >
                          <Icon className={`h-4 w-4 ${action.iconColor}`} />
                          {action.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {pendingCount > 1 && !justUpdated && (
            <div className="flex items-center justify-center gap-1.5 mt-4">
              {appointments.map((_, i) => (
                <div
                  key={i}
                  className={`h-1.5 rounded-full transition-all ${
                    i === 0 ? 'w-6 bg-gray-800' : 'w-1.5 bg-gray-300'
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
};

export default PendingAppointmentsCard;
